// Chrome for the boat-scoped routes. Owns the one device-inventory subscription
// for the boat, the boat-level live telemetry, the steady-state refresh poll that
// keeps the presence chip and inventory current, and the search + manual sheet
// state, exposing them to pages and cards via BoatChromeContext.
//
// The layout is the Marina look's (cloud ADR 0396 §5, TOM-677), on the Neomaris
// v2 app prototype (TOM-193) and the Neo home handoff (TOM-513): a header with
// Neo's headline (TOM-526, where the quick readings were) and the mode button;
// a sidebar on the left above 768 px that opens with its labels and closes to
// its icons (this browser remembers which); the bottom bar of Home, Chats and the boards at 768 px
// and under, home included; and a menu with every entry that slides in from
// the left at every width. The boat opens on home (TOM-510). The web-only
// screens sit in the menu's Boat group.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuth, useDemoGuest } from '../../auth';
import { boatPresenceLabel, getBoatPresence } from '../../lib/boatPresence';
import { isSparkleRoute, readHomeChatOpen, writeHomeChatOpen } from '../../lib/homeChat';
import {
  SEEN_STAMP_MS,
  greetingDue,
  readBoatSeen,
  requestGreeting,
  writeBoatSeen,
} from '../../lib/homeGreeting';
import { askNeoSuggestions } from '../../lib/homeHeader';
import { isSlideChatRoute } from '../../lib/neoSlideChat';
import { announceNeoGreetingPending, announceNeoMainMessage } from '../../lib/neoMainChat';
import { useBoatAlerts } from '../../lib/useBoatAlerts';
import { useBoatDevices, type VesselDevice } from '../../lib/useBoatDevices';
import { useBoatLiveData } from '../../lib/useBoatLiveData';
import { useBoatInfo } from '../../lib/useBoatInfo';
import { useBoatNames, useBoatNamesRevision } from '../../lib/useBoatNames';
import { useNeoBoards } from '../../lib/useNeoBoards';
import { useBoatHasCameras } from '../../lib/useCameras';
import { useBoats } from '../../lib/useBoats';
import { PHONE_QUERY, useMediaQuery } from '../../lib/useMediaQuery';
import { useNeoStatus } from '../../lib/useNeoStatus';
import { markDemoGuest } from '../../lib/demo';
import { useLeaveDemo } from '../../lib/useLeaveDemo';
import { readSidebarCompact, writeSidebarCompact } from '../../lib/sidebarCompact';
import { NeoChat } from '../NeoChat';
import { SlideChat } from '../neo/SlideChat';
import { SparkleChat } from '../neo/SparkleChat';
import { ManualSheet } from '../manuals/ManualSheet';
import { SearchOverlay } from '../manuals/SearchOverlay';
import { AnchorPromptBanner } from './AnchorPromptBanner';
import {
  BoatChromeContext,
  HomeChromeContext,
  type BoatChrome,
  type HomeChrome,
} from './boatChrome';
import { BoatDetailsDialog, type BoatDetailsAnchor } from './BoatDetailsDialog';
import { BoatFirmwareBanner } from './BoatFirmwareBanner';
import { BoatHeader } from './BoatHeader';
import { BoatSideNav, BoatTabBar } from './BoatNav';
import { BoatSidebar } from './BoatSidebar';
import { modeBoardKey, systemBoardFor } from '../../lib/modeBoards';
import { useAnchorWatchOn } from '../../lib/useAnchorWatchOn';
import { BoatUpdateBanner } from './BoatUpdateBanner';
import { DemoBanner } from './DemoBanner';
import { BrandBar } from './BrandBar';
import { ModeSelector } from './ModeSelector';
import { PlaybackBanner } from './PlaybackBanner';

// Steady-state refresh cadence. `last_contacted_at` only advances server-side
// every ~30s (boat-ws coalescing), so 30s pairs with the 90s presence window.
const STEADY_REFRESH_MS = 30_000;

export function BoatShell() {
  const { boatId = '' } = useParams<{ boatId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  // A Try it guest (TOM-868): the banner shows, and what it cannot use is hidden.
  const guest = useDemoGuest();
  const leaveDemo = useLeaveDemo();
  const [menuOpen, setMenuOpen] = useState(false);
  // The left sidebar opens with its labels; closed to its icons, this browser
  // remembers it (Mike, 2026-10-16).
  const [sidebarCompact, setSidebarCompact] = useState(readSidebarCompact);
  const toggleSidebarCompact = useCallback(() => {
    setSidebarCompact((was) => {
      writeSidebarCompact(!was);
      return !was;
    });
  }, []);
  // Home's chat, docked over the board wider than 768 px (TOM-920): open or
  // closed per boat as the owner left it, opened for Neo's greeting.
  const [homeChatOpen, setHomeChatOpenState] = useState(() => readHomeChatOpen(boatId));
  useEffect(() => setHomeChatOpenState(readHomeChatOpen(boatId)), [boatId]);
  const setHomeChatOpen = useCallback(
    (open: boolean) => {
      setHomeChatOpenState(open);
      writeHomeChatOpen(boatId, open);
    },
    [boatId]
  );
  // While it is open the nav shows its icons only, whatever the owner keeps
  // (TOM-920). Its toggle shows the labels for this visit, and the owner's
  // own choice stays as it is.
  const [labelsOverDock, setLabelsOverDock] = useState(false);
  useEffect(() => {
    if (homeChatOpen) setLabelsOverDock(false);
  }, [homeChatOpen]);
  // The mode selector, open from one button at a time (TOM-526).
  const [modeFrom, setModeFrom] = useState<{
    button: HTMLElement;
    align: 'start' | 'end';
  } | null>(null);
  const boats = useBoats();
  const devices = useBoatDevices(boatId);
  const alerts = useBoatAlerts(boatId);
  // The registry signal fires on claim/delete/deregister ("boat list changed")
  // — NOT on ongoing boat contact; the steady-state poll below covers that.
  // `removed` (TOM-458) means another owner removed this person from the boat,
  // or they left it on another device: the boat drops out of the list, and the
  // page says why rather than calling it not found.
  const [removedFrom, setRemovedFrom] = useState<string | null>(null);
  const live = useBoatLiveData(boatId === '' ? [] : [boatId], {
    onRegistry: (event) => {
      // A message reached the owner's main chat from elsewhere, such as an
      // alarm Neomaris posted (TOM-517): the chats showing it read it in.
      if (event.kind === 'neo_main_message') {
        if (event.boatId !== undefined) announceNeoMainMessage(event.boatId);
        return;
      }
      if (event.kind === 'removed' && event.boatId !== undefined) setRemovedFrom(event.boatId);
      boats.refresh();
    },
  });

  // Neo greets the owner on home (TOM-920): on a first visit to the boat in
  // this browser, and after at least four hours away from its pages. The time
  // is stamped while a page of the boat is on screen and when the tab hides.
  const [greetFor, setGreetFor] = useState<string | null>(null);
  useEffect(() => {
    if (boatId === '') return;
    if (greetingDue(readBoatSeen(boatId), Date.now())) setGreetFor(boatId);
    writeBoatSeen(boatId, Date.now());
    const stamp = () => {
      if (document.visibilityState === 'visible') writeBoatSeen(boatId, Date.now());
    };
    const id = window.setInterval(stamp, SEEN_STAMP_MS);
    const onVisibility = () => {
      const now = Date.now();
      if (document.visibilityState === 'visible' && greetingDue(readBoatSeen(boatId), now)) {
        setGreetFor(boatId);
      }
      writeBoatSeen(boatId, now);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      writeBoatSeen(boatId, Date.now());
    };
  }, [boatId]);

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sheetDevice, setSheetDevice] = useState<VesselDevice | null>(null);
  const [boatDetailsOpen, setBoatDetailsOpen] = useState(false);
  const [boatDetailsAnchor, setBoatDetailsAnchor] = useState<BoatDetailsAnchor>({
    left: 24,
    top: 140,
  });
  const boatDetailsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const sidebarBoatRef = useRef<HTMLButtonElement | null>(null);
  // What opened the sidebar, for focus to return to when it is dismissed.
  const sidebarOpenerRef = useRef<HTMLElement | null>(null);
  // Above 768 px the sidebar's More leads to the menu; at 768 px and under the
  // bottom bar takes the sidebar's place, and a menu button in the header does.
  const phone = useMediaQuery(PHONE_QUERY);
  // The boat's own answer about its mode (ADR 0188), for the header's mode button.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Steady-state safety poll: the boat record (presence chip source) and the
  // device inventory are otherwise only snapshots from mount time — nothing
  // refetches them on boat contact — so without this the chip decays to
  // Offline after 90s and the inventory freezes. Paused while the tab is
  // hidden; an immediate refresh on return catches the tab back up. Both
  // refresh fns are stable useCallbacks and neither hook flashes `loading`
  // on refresh.
  const refreshBoats = boats.refresh;
  const refreshDevices = devices.refresh;
  const refreshAlerts = alerts.refresh;
  useEffect(() => {
    let id: number | null = null;
    const refresh = () => {
      refreshBoats();
      refreshDevices();
      refreshAlerts();
    };
    const start = () => {
      if (id === null) id = window.setInterval(refresh, STEADY_REFRESH_MS);
    };
    const stop = () => {
      if (id !== null) {
        clearInterval(id);
        id = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        refresh();
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [refreshBoats, refreshDevices, refreshAlerts]);

  const boat =
    boats.state.kind === 'ready' ? (boats.state.boats.find((b) => b.id === boatId) ?? null) : null;

  const closeBoatDetails = useCallback((restoreFocus = true) => {
    setBoatDetailsOpen(false);
    if (!restoreFocus) return;
    window.requestAnimationFrame(() => {
      // The sidebar's boat row, which stays open under the details. When the
      // sidebar has closed, focus goes to the menu button that leads to it.
      const trigger = [
        boatDetailsTriggerRef.current,
        sidebarBoatRef.current,
        visibleMenuButton(),
      ].find((candidate) => candidate?.isConnected && candidate.getClientRects().length > 0);
      trigger?.focus();
    });
  }, []);

  const openBoatDetails = useCallback((event: ReactMouseEvent<HTMLButtonElement>) => {
    const trigger = event.currentTarget;
    const rect = trigger.getBoundingClientRect();
    const panelWidth = Math.min(360, window.innerWidth - 32);
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - panelWidth - 16));
    boatDetailsTriggerRef.current = trigger;
    // The desktop panel opens under the sidebar's boat row. On a phone the
    // details are a bottom sheet. The sidebar stays open beneath either, so
    // closing the details returns to the row that opened them.
    setBoatDetailsAnchor({ left, top: rect.bottom + 8 });
    // Only one modal/sheet may own focus and Escape at a time.
    setSearchOpen(false);
    setSheetDevice(null);
    setBoatDetailsOpen(true);
  }, []);

  // The menu (TOM-513) opens from the sidebar's More, the header's menu
  // button on a phone, and home's. Dismissed, it gives focus back to the one
  // that opened it, or to the menu button on screen when that one is gone.
  const openSidebar = useCallback(() => {
    sidebarOpenerRef.current =
      document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : null;
    setMenuOpen(true);
  }, []);
  const dismissSidebar = useCallback(() => {
    setMenuOpen(false);
    window.requestAnimationFrame(() => {
      const opener = sidebarOpenerRef.current;
      const target =
        opener !== null && opener.isConnected && opener.getClientRects().length > 0
          ? opener
          : visibleMenuButton();
      target?.focus();
    });
  }, []);

  // Route changes (including a details action) and a removed boat make the
  // current trigger stale. Close without trying to return focus to it.
  useEffect(() => {
    setBoatDetailsOpen(false);
    setMenuOpen(false);
    setModeFrom(null);
  }, [boatId, location.pathname]);

  // The mode selector opens under the button that opened it (home's board
  // name, its phone mode button, or the header's mode button), and gives the
  // focus back to it at once on close: a window in the background gets no frames.
  const openMode = useCallback((button: HTMLElement, align: 'start' | 'end' = 'start') => {
    setMenuOpen(false);
    setBoatDetailsOpen(false);
    setSearchOpen(false);
    setModeFrom({ button, align });
  }, []);
  const modeFromRef = useRef(modeFrom);
  modeFromRef.current = modeFrom;
  const closeMode = useCallback(() => {
    const from = modeFromRef.current;
    setModeFrom(null);
    from?.button.focus();
  }, []);
  useEffect(() => {
    if (boats.state.kind === 'ready' && boat === null) setBoatDetailsOpen(false);
  }, [boat, boats.state.kind]);

  // Stable, so the overlay's open effect (focus, Escape) runs once per opening
  // rather than on every tick of the shell's clock.
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  // The overlay carries its own input at every width: the sidebar's search row
  // closes the sidebar and opens it, and so does Cmd/Ctrl-K.
  const openSearch = useCallback((initial?: string) => {
    setBoatDetailsOpen(false);
    setMenuOpen(false);
    if (initial !== undefined) setQuery(initial);
    setSearchOpen(true);
  }, []);

  const openManual = useCallback((device: VesselDevice) => {
    setBoatDetailsOpen(false);
    const manuals = device.manuals ?? [];
    const first = manuals[0];
    if (manuals.length === 1 && first) {
      window.open(first.downloadUrl, '_blank', 'noopener');
      return;
    }
    if (manuals.length > 1) setSheetDevice(device);
  }, []);

  // Cmd/Ctrl-K focuses the search from anywhere on a boat route.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openSearch();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openSearch]);

  const samples = live.latest.get(boatId);
  const lastSampleAtMs = live.lastSampleAt.get(boatId);
  const infoRevision = live.infoRevision.get(boatId) ?? 0;
  const anchorCommandStatus = live.anchorCommandStatus.get(boatId);
  const boardChanged = live.boardChanged.get(boatId);
  const looksChanged = live.looksChanged.get(boatId);
  const localNamesRevision = useBoatNamesRevision(boatId);
  const equipmentRevision = (live.equipmentRevision.get(boatId) ?? 0) + localNamesRevision;
  const namesRevision = (live.namesRevision.get(boatId) ?? 0) + localNamesRevision;
  const names = useBoatNames(boatId, namesRevision);
  useEffect(() => {
    if (namesRevision > 0) {
      refreshDevices();
      refreshAlerts();
    }
  }, [namesRevision, refreshDevices, refreshAlerts]);
  const boards = useNeoBoards(boatId, boardChanged?.seq ?? 0);
  // A guest is refused the cameras read, and its menu has no Cameras row (TOM-868).
  const hasCameras = useBoatHasCameras(guest ? null : boatId);
  const anchorWatchOn = useAnchorWatchOn(boatId);
  const inventory = devices.state.kind === 'ready' ? devices.state.devices : [];
  const info = useBoatInfo(boatId, infoRevision);
  const navState = info?.navigation?.state ?? null;
  const archetype = info === null ? undefined : (navState?.state ?? null);
  // The bar's mode slot (TOM-358): the board for the boat's mode, by key.
  const systemBoards = boards.state.kind === 'ready' ? boards.state.systemBoards : [];
  const modeBoard = systemBoardFor(
    systemBoards,
    modeBoardKey(navState?.state ?? null, anchorWatchOn)
  );

  // Neo's status, whose headline every header shows (TOM-526) and whose body
  // home shows above its board (TOM-512). It is read again at once when the
  // boat's mode, its alarms or its anchor watch change, and every minute
  // besides (useNeoStatus).
  const alarmKey =
    alerts.state.kind === 'ready'
      ? alerts.state.response.alerts
          .map((r) => `${r.definition.id}:${r.runtime?.state}:${r.runtime?.lastTransitionAt}`)
          .join(',')
      : alerts.state.kind;
  const statusKey = [
    navState?.mode,
    navState?.modeSince,
    alerts.currentMode,
    alarmKey,
    anchorWatchOn,
    anchorCommandStatus?.phase === 'complete' ? anchorCommandStatus.commandKey : '',
  ].join('|');
  const status = useNeoStatus(boatId, statusKey);

  const ctx = useMemo<BoatChrome>(
    () => ({
      boatId,
      boat,
      devices,
      alerts,
      liveSamples: samples,
      liveStore: live.liveStore,
      lastSampleAtMs,
      liveStatus: live.status,
      infoRevision,
      anchorCommandStatus,
      boardChanged,
      equipmentRevision,
      namesRevision,
      looksChanged,
      names,
      boards,
      modeBoard,
      archetype,
      openManual,
      openSearch,
      refreshBoats,
    }),
    [
      boatId,
      boat,
      devices,
      alerts,
      samples,
      live.liveStore,
      lastSampleAtMs,
      live.status,
      infoRevision,
      anchorCommandStatus,
      boardChanged,
      equipmentRevision,
      namesRevision,
      looksChanged,
      names,
      boards,
      modeBoard,
      archetype,
      openManual,
      openSearch,
      refreshBoats,
    ]
  );

  // The greeting, once home is on screen: the docked chat opens for it, and on
  // a phone the sparkle's dot says it came. A Try it guest's greeting is one
  // of their Neo turns (cloud ADR 0452).
  const onHome =
    location.pathname === `/dashboard/boats/${boatId}` ||
    location.pathname === `/dashboard/boats/${boatId}/`;
  useEffect(() => {
    if (greetFor !== boatId || !onHome) return;
    setGreetFor(null);
    if (!phone) setHomeChatOpen(true);
    // Said before the request, so a greeting that lands before its answer is
    // still the one the chat waits for.
    announceNeoGreetingPending(boatId);
    void requestGreeting(boatId).then((queued) => {
      if (!queued) announceNeoGreetingPending(boatId, false);
    });
  }, [greetFor, boatId, onHome, phone, setHomeChatOpen]);

  // Nav badge: how many alarms currently need attention + the tone to draw them
  // in. Rendered only when the count is > 0 (no zero-badge noise).
  const alarmBadge =
    alerts.state.kind === 'ready' && alerts.state.activeCount > 0
      ? { count: alerts.state.activeCount, tone: alerts.state.badgeTone }
      : null;

  // The boat genuinely isn't ours (or was deleted): a clear not-found, not an
  // empty inventory. (The existing device-discovery e2e asserts this heading.)
  if (boats.state.kind === 'ready' && boat === null) {
    const removed = removedFrom === boatId;
    return (
      <>
        <BrandBar />
        <main className="neo-shell">
          <section className="dashboard">
            <header className="dashboard-head">
              <h2>{removed ? 'You’re no longer an owner of this boat' : 'Boat not found'}</h2>
              <p className="dashboard-sub" data-testid={removed ? 'boat-removed' : undefined}>
                {removed
                  ? 'An owner removed you from this boat, or you left it. Ask an owner to invite you again.'
                  : 'This boat isn’t paired with your account, it was deleted, or you are no longer one of its owners.'}
              </p>
            </header>
            <p>
              <Link to="/dashboard">← Back to your boats</Link>
            </p>
          </section>
        </main>
      </>
    );
  }

  const boatName = boat?.display_name ?? 'Boat';
  const presence = boat
    ? getBoatPresence(boat.last_contacted_at, boat.created_at, lastSampleAtMs, now)
    : 'offline';
  const presenceLabel = boatPresenceLabel(presence);

  // The chip is one predictable boat-details trigger at every fleet size.
  // Multi-boat switching remains an explicit action inside the details panel.
  const canSwitchBoats = boats.state.kind === 'ready' && boats.state.boats.length > 1;
  const presenceDot = (
    <span
      className={`boat-status-dot boat-status-dot--${presence}`}
      aria-hidden="true"
      title={presenceLabel}
      data-testid={`presence-${boatId}-${presence}`}
    />
  );
  const menuPresenceDot = (
    <span className={`boat-status-dot boat-status-dot--${presence}`} aria-hidden="true" />
  );

  const boatTo = `/dashboard/boats/${boatId}`;
  const toolkitRoute = location.pathname.includes('/toolkit');
  // At anchor's board is the only surface that height-locks to a single
  // landscape view (its watch bento); the modifier scopes that CSS to it so
  // other boat routes keep their normal scroll.
  // At anchor's board draws the same bento (TOM-358), and Underway its
  // landscape ladder (TOM-856), which takes the same room.
  const composedSlugs =
    boards.state.kind === 'ready'
      ? boards.state.systemBoards
          .filter((b) => b.systemKey === 'anchored' || b.systemKey === 'underway')
          .map((b) => b.slug)
      : [];
  const dashboardRoute = composedSlugs.some(
    (slug) => location.pathname === `${boatTo}/boards/${encodeURIComponent(slug)}`
  );
  // Every other board page takes the same width, so its grid has room (TOM-856).
  const boardRoute = location.pathname.startsWith(`${boatTo}/boards/`);
  // The boat opens on home (TOM-510, cloud ADR 0335): the mode's board under
  // its own fixed header, which takes the shell header's place. On a phone
  // the bottom bar stays under home too (ADR 0396 §5).
  const homeRoute = location.pathname === boatTo || location.pathname === `${boatTo}/`;
  // The chat fills the content area and scrolls inside itself.
  const chatRoute =
    location.pathname === `${boatTo}/neo` || location.pathname.startsWith(`${boatTo}/neo/`);
  // The AI Watch page embeds its own Neo thread (ADR 0196) and the analyzer has
  // its inline launcher; the chat page IS Neo, and home hosts the main chat
  // (TOM-511), so exactly one chat answers there. Everywhere else the Neo panel
  // is mounted without a launcher, so the dashboard's "Ask Neo" buttons and its
  // background auto-compose (the `nm:open-neo` event) still reach it.
  const watchRoute = location.pathname.includes('/watch');
  const neoPanel = !chatRoute && !watchRoute && !toolkitRoute && !homeRoute;
  // Home's sparkle on Alarms, an alarm's page and every board page (TOM-547):
  // the main chat answers there. A board page keeps the Edit chat for the
  // requests that name its board; an alarm page has no other chat.
  const sparkleRoute = isSparkleRoute(location.pathname, boatTo);
  const boardPage = location.pathname.startsWith(`${boatTo}/boards/`);
  // Wider than 768 px Neo's full chat slides in over every page but the chat
  // itself and the toolkit (TOM-738); the Edit chat keeps the board's edits.
  const slideRoute = !phone && isSlideChatRoute(location.pathname, boatTo);
  // The mode's suggested questions, above every Neo chat's message box (TOM-741, TOM-817).
  const neoSuggestions = askNeoSuggestions(navState?.state ?? null, anchorWatchOn);
  const editChat = slideRoute ? neoPanel && boardPage : neoPanel && (!sparkleRoute || boardPage);
  const modeName = navState?.mode ?? null;
  const ownerName = auth.user?.displayName ?? auth.user?.email ?? 'Account';
  const boatDetailsLabel = boat
    ? `${boatName}, ${presenceLabel} — view boat details`
    : 'Boat details loading';

  // Neo's headline opens the chat (TOM-526): the Neo panel where it is
  // mounted; on the chat page, its own field; elsewhere, the chat page.
  function openNeoFromHeadline() {
    if (neoPanel || slideRoute) {
      window.dispatchEvent(new CustomEvent('nm:open-neo'));
      return;
    }
    const field = document.querySelector<HTMLTextAreaElement>('textarea[aria-label="Message Neo"]');
    if (field !== null) field.focus();
    else navigate(`${boatTo}/neo`);
  }

  const dockedHome = slideRoute && homeRoute;
  const navCompact = dockedHome && homeChatOpen ? !labelsOverDock : sidebarCompact;
  const toggleNav = () => {
    if (dockedHome && homeChatOpen) setLabelsOverDock((was) => !was);
    else toggleSidebarCompact();
  };

  const home: HomeChrome = {
    presence,
    presenceLabel,
    status,
    menuOpen,
    openMenu: openSidebar,
    modeOpenFrom: modeFrom?.button ?? null,
    openMode,
    boatState: navState,
    anchorWatchOn,
    slideChat: slideRoute,
    homeChat: { docked: dockedHome, open: homeChatOpen, setOpen: setHomeChatOpen },
  };

  async function signOut() {
    setMenuOpen(false);
    // A Try it guest's Sign out also ends its lease (TOM-868).
    if (guest) {
      await leaveDemo();
      return;
    }
    markDemoGuest(false);
    await auth.logout();
    navigate('/');
  }

  return (
    <BoatChromeContext.Provider value={ctx}>
      <div
        className={`boat-app${chatRoute ? ' boat-app--chat' : ''}${homeRoute ? ' boat-app--home' : ''}${navCompact ? ' boat-app--compact' : ''}`}
      >
        {phone ? null : (
          <BoatSideNav
            boatId={boatId}
            boatName={boatName}
            presence={presence}
            presenceDot={presenceDot}
            presenceLabel={presenceLabel}
            alarmBadge={alarmBadge}
            boards={boards.state}
            hasCameras={hasCameras === true && !guest}
            compact={navCompact}
            onToggleCompact={toggleNav}
            sidebarOpen={menuOpen}
            onSidebar={openSidebar}
          />
        )}

        <div className="boat-app__main">
          {homeRoute ? null : (
            <BoatHeader
              status={status.kind === 'ready' ? status.status : null}
              onHeadline={openNeoFromHeadline}
              archetype={navState?.state ?? null}
              modeLabel={modeName ?? 'Let the boat decide'}
              modeOpen={modeFrom !== null}
              menuOpen={menuOpen}
              onMode={(e) => openMode(e.currentTarget, 'end')}
              onMenu={phone ? openSidebar : undefined}
            />
          )}

          <main
            className={`neo-shell ${toolkitRoute ? 'neo-shell--toolkit' : ''} ${
              dashboardRoute ? 'neo-shell--dashboard' : boardRoute ? 'neo-shell--board' : ''
            } ${chatRoute ? 'neo-shell--chat' : ''} ${homeRoute ? 'neo-shell--home' : ''}`}
          >
            {/* The boat's anchor question, first because it has a clock on it and
                the boat acts when it runs out. Renders nothing unless the boat is
                actually asking. */}
            <DemoBanner />
            {/* A guest cannot answer the boat's anchor question (TOM-868). */}
            {guest ? null : (
              <AnchorPromptBanner boatId={boatId} liveSamples={samples} nowMs={now} />
            )}
            {/* The update offer and its outcome, above the route content so a
                failure is visible without scrolling. This is the ONLY way a
                single-boat owner reaches the approval. */}
            {boat && !guest ? (
              <BoatUpdateBanner
                boat={boat}
                coreLatest={boat.coreUpdateAvailable ?? null}
                onRefresh={refreshBoats}
              />
            ) : null}
            {/* The firmware twin (ADR 0202). Renders nothing on every other boat. */}
            {boat && !guest ? (
              <BoatFirmwareBanner key={boat.id} boat={boat} onRefresh={refreshBoats} />
            ) : null}
            {/* Playback indicator, presence-gated (a crashed playback boat's stale
                claim cannot leave a banner behind). */}
            {boat && presence !== 'offline' ? (
              <PlaybackBanner sim={boat.simTime} now={now} />
            ) : null}
            <HomeChromeContext.Provider value={home}>
              <Outlet />
            </HomeChromeContext.Provider>
          </main>
        </div>

        {slideRoute ? (
          <SlideChat
            boatId={boatId}
            boardPage={boardPage}
            suggestions={neoSuggestions}
            fullChatHref={`${boatTo}/neo`}
            dock={dockedHome ? { open: homeChatOpen, onOpenChange: setHomeChatOpen } : undefined}
          />
        ) : null}

        {phone ? (
          <BoatTabBar
            boatId={boatId}
            alarmBadge={alarmBadge}
            boards={boards.state}
            modeBoard={modeBoard}
          />
        ) : null}
      </div>

      <BoatSidebar
        open={menuOpen}
        boatId={boatId}
        boatName={boatName}
        presenceDot={menuPresenceDot}
        presenceLabel={presenceLabel}
        ownerName={ownerName}
        alarmBadge={alarmBadge}
        boards={boards.state}
        boatDetailsRef={sidebarBoatRef}
        boatDetailsLabel={boatDetailsLabel}
        boatDetailsDisabled={boat === null}
        boatDetailsOpen={boatDetailsOpen}
        onBoatDetails={openBoatDetails}
        onSearch={() => openSearch()}
        onSignOut={() => void signOut()}
        guest={guest}
        onClose={dismissSidebar}
      />
      {modeFrom !== null ? (
        <ModeSelector
          boatId={boatId}
          anchor={modeFrom.button}
          align={modeFrom.align}
          currentMode={modeName}
          pinned={navState?.detectedBy === 'manual'}
          guest={guest}
          onClose={closeMode}
        />
      ) : null}

      {boat ? (
        <BoatDetailsDialog
          boat={boat}
          presence={presence}
          now={now}
          canSwitchBoats={canSwitchBoats}
          open={boatDetailsOpen}
          anchor={boatDetailsAnchor}
          onDismiss={closeBoatDetails}
        />
      ) : null}

      <SearchOverlay
        open={searchOpen}
        ownInput
        query={query}
        devices={inventory}
        boatId={boatId}
        onQuery={setQuery}
        onClose={closeSearch}
      />
      <ManualSheet device={sheetDevice} onClose={() => setSheetDevice(null)} />
      {editChat ? <NeoChat launcher={false} editOnly={sparkleRoute || slideRoute} /> : null}
      {sparkleRoute && phone ? <SparkleChat boatId={boatId} suggestions={neoSuggestions} /> : null}
    </BoatChromeContext.Provider>
  );
}

/** The menu button on screen: the sidebar's More, the header's or home's. */
function visibleMenuButton(): HTMLButtonElement | null {
  const buttons = document.querySelectorAll<HTMLButtonElement>('[data-testid="boat-menu-button"]');
  return Array.from(buttons).find((b) => b.getClientRects().length > 0) ?? null;
}
